import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import {
  SparklesIcon,
  HeartIcon,
  ArrowUpRightIcon,
  CodeBracketIcon,
  CubeIcon,
  MusicalNoteIcon,
  VideoCameraIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import { promptsAPI, featuredProjectsAPI } from '../services/api';
import { Prompt, PromptCategory } from '../types';
import AdSenseAd from '../components/AdSenseAd';
import FeaturedProjectAdCard from '../components/FeaturedProjectAdCard';
import { PromptCardGrid } from '../components/PromptCardGrid';

/* ─── Prompt Grid (below) renders all recent prompts ──────────────────────────────────────────────── */

/* ─── Section Header ───────────────────────────────────────────── */
const SectionHeader: React.FC<{
  label: string;
  title: string;
  subtitle?: string;
  href: string;
  linkLabel?: string;
}> = ({ label, title, subtitle, href, linkLabel = 'Ver todos' }) => (
  <div className="flex items-end justify-between mb-8">
    <div>
      <span className="mono-label" style={{ marginBottom: 8 }}>{label}</span>
      <h2
        style={{
          fontSize: 24,
          fontWeight: 700,
          letterSpacing: '-0.025em',
          marginBottom: subtitle ? 4 : 0,
        }}
      >
        {title}
      </h2>
      {subtitle && (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 3 }}>{subtitle}</p>
      )}
    </div>
    <Link
      to={href}
      className="flex items-center gap-1 nav-link"
      style={{ fontSize: 12, flexShrink: 0 }}
    >
      {linkLabel}
      <ArrowUpRightIcon className="h-3 w-3" />
    </Link>
  </div>
);

/* ─── Prompt Grid ──────────────────────────────────────────────── */
const PromptGrid: React.FC<{ prompts: Prompt[] }> = ({ prompts }) => {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());

  const handleFavorite = (id: string) => {
    const newFavorites = new Set(favorites);
    if (newFavorites.has(id)) {
      newFavorites.delete(id);
    } else {
      newFavorites.add(id);
    }
    setFavorites(newFavorites);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {prompts.map((prompt, i) => (
        <div key={prompt._id}>
          <PromptCardGrid
            prompt={prompt}
            isFavorited={favorites.has(prompt._id)}
            onFavorite={handleFavorite}
            delay={i * 55}
          />
        </div>
      ))}
    </div>
  );
};

/* ─── Categories ───────────────────────────────────────────────── */
const categories: { value: PromptCategory; label: string; icon: React.ElementType }[] = [
  { value: 'text-generation',  label: 'Linguistic', icon: SparklesIcon     },
  { value: 'image-generation', label: 'Visual',     icon: CubeIcon         },
  { value: 'code-generation',  label: 'Logic',      icon: CodeBracketIcon  },
  { value: 'audio-generation', label: 'Acoustic',   icon: MusicalNoteIcon  },
  { value: 'video-generation', label: 'Kinetic',    icon: VideoCameraIcon  },
  { value: 'data-analysis',    label: 'Analytic',   icon: ChartBarIcon     },
];

/* ─── Terminal Preview Card ────────────────────────────────────── */
const TerminalPreview: React.FC<{ prompt?: Prompt }> = ({ prompt }) => (
  <div
    className="animate-fade-up"
    style={{ animationDelay: '120ms' }}
  >
    {/* Ambient glow behind the card */}
    <div
      style={{
        position: 'absolute',
        inset: -40,
        background: 'radial-gradient(ellipse 70% 60% at 60% 50%, rgba(232,184,75,0.07) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
    <div
      style={{
        background: 'var(--surface-1)',
        border: '1px solid var(--border-emphasis)',
        borderRadius: 'var(--r-md)',
        overflow: 'hidden',
        boxShadow: '0 0 0 1px rgba(232,184,75,0.06), 0 8px 48px rgba(0,0,0,0.7)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      {/* Terminal bar */}
      <div
        style={{
          background: 'var(--surface-3)',
          borderBottom: '1px solid var(--border-default)',
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {['#6B2D2D', '#6B5C2D', '#2D4B35'].map((c) => (
          <div key={c} style={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
        ))}
        <span
          style={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 10,
            color: 'var(--text-secondary)',
            marginLeft: 10,
            letterSpacing: '0.06em',
            opacity: 0.6,
          }}
        >
          prompt.txt
        </span>
      </div>

      <div style={{ padding: '20px' }}>
        {prompt ? (
          <>
            <div className="flex items-center gap-2 mb-3">
              <span className="chip chip-signal">{prompt.aiTool}</span>
              <span className="chip">{prompt.category.replace(/-/g, ' ')}</span>
            </div>
            <p
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--text-primary)',
                letterSpacing: '-0.02em',
                marginBottom: 14,
                lineHeight: 1.35,
              }}
            >
              {prompt.title}
            </p>
            <div
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11,
                color: 'var(--text-secondary)',
                lineHeight: 1.75,
                padding: '12px 14px',
                background: 'var(--base)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-sm)',
              }}
            >
              <span style={{ color: 'var(--signal)', marginRight: 8, opacity: 0.9 }}>›</span>
              {(prompt.prompt || prompt.description || '').slice(0, 220)}
              {(prompt.prompt || prompt.description || '').length > 220 && (
                <span style={{ color: 'var(--text-secondary)', opacity: 0.35 }}>…</span>
              )}
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-1.5">
                <StarIcon className="h-3 w-3" style={{ color: 'var(--signal)' }} />
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace' }}>
                  {prompt.ratings.average.toFixed(1)}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <HeartIcon className="h-3 w-3" style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', opacity: 0.6, fontFamily: 'JetBrains Mono, monospace' }}>
                  {prompt.favorites}
                </span>
              </div>
            </div>
          </>
        ) : (
          <div style={{ minHeight: 160 }}>
            <div className="flex items-center gap-2 mb-3">
              <span className="chip chip-signal">ChatGPT</span>
              <span className="chip">Text Generation</span>
            </div>
            <p
              style={{
                fontFamily: 'Plus Jakarta Sans, sans-serif',
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--text-primary)',
                letterSpacing: '-0.02em',
                marginBottom: 14,
                lineHeight: 1.35,
              }}
            >
              Professional Email Writing Guide
            </p>
            <div
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: 11,
                color: 'var(--text-secondary)',
                lineHeight: 1.75,
                padding: '12px 14px',
                background: 'var(--base)',
                border: '1px solid var(--border-default)',
                borderRadius: 'var(--r-sm)',
              }}
            >
              <span style={{ color: 'var(--signal)', marginRight: 8, opacity: 0.9 }}>›</span>
              Write professional emails in seconds. Provide context and tone preference. Get grammatically perfect, persuasive emails...
            </div>
            <div className="flex items-center gap-3 mt-4">
              <div className="flex items-center gap-1.5">
                <StarIcon className="h-3 w-3" style={{ color: 'var(--signal)' }} />
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'JetBrains Mono, monospace' }}>
                  4.8
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <HeartIcon className="h-3 w-3" style={{ color: 'var(--text-secondary)', opacity: 0.5 }} />
                <span style={{ fontSize: 11, color: 'var(--text-secondary)', opacity: 0.6, fontFamily: 'JetBrains Mono, monospace' }}>
                  342
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  </div>
);

/* ─── Home ─────────────────────────────────────────────────────── */
const Home: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const [featuredPrompts, setFeaturedPrompts] = useState<Prompt[]>([]);
  const [recentPrompts, setRecentPrompts]     = useState<Prompt[]>([]);
  const [featuredProjects, setFeaturedProjects] = useState<any[]>([]);
  const [loading, setLoading]                 = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const [featured, recent, projects] = await Promise.all([
          promptsAPI.getPrompts({ featured: true, limit: 6 }),
          promptsAPI.getPrompts({ sort: 'newest', limit: 6 }),
          featuredProjectsAPI.getRandomProjects(3),
        ]);
        setFeaturedPrompts(featured.prompts || []);
        setRecentPrompts(recent.prompts || []);
        setFeaturedProjects(projects.projects || []);
      } catch { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return (
      <div
        className="flex items-center justify-center"
        style={{ minHeight: '70vh', background: 'var(--base)' }}
      >
        <div className="flex flex-col items-center gap-4">
          <div className="spinner" />
          <span
            style={{
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 10,
              color: 'var(--text-secondary)',
              letterSpacing: '0.14em',
              opacity: 0.5,
            }}
          >
            {t('common.loading').toUpperCase()}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--base)', minHeight: '100vh' }}>
      <SEO
        title="Axioma - Prompts de IA y Comunidad Tech"
        description="Descubre miles de prompts de IA, herramientas y únete a nuestra comunidad de tech enthusiasts"
        canonical="https://axioma.javzdev.com"
      />

      {/* ─── Hero / Personalized Feed ──────────────────────────── */}
      {!isAuthenticated ? (
        <section style={{ padding: '88px 0 96px', position: 'relative', overflow: 'hidden' }}>
          {/* Hero glow — indigo blob top-right */}
          <div
            style={{
              position: 'absolute',
              top: '-15%', right: '-8%',
              width: '52%', height: '130%',
              background: 'radial-gradient(ellipse, rgba(79,70,229,0.12) 0%, rgba(99,102,241,0.06) 40%, transparent 70%)',
              pointerEvents: 'none',
              filter: 'blur(1px)',
            }}
          />
          {/* Hero glow — violet blob bottom-left */}
          <div
            style={{
              position: 'absolute',
              bottom: '-10%', left: '-5%',
              width: '40%', height: '80%',
              background: 'radial-gradient(ellipse, rgba(59,130,246,0.08) 0%, transparent 65%)',
              pointerEvents: 'none',
            }}
          />

          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">

              {/* Left — editorial copy */}
              <div className="animate-fade-up">
                <div
                  className="flex items-center gap-2"
                  style={{
                    display: 'inline-flex',
                    padding: '5px 12px 5px 8px',
                    border: '1px solid rgba(79,70,229,0.20)',
                    borderRadius: 'var(--r-lg)',
                    marginBottom: 28,
                    background: 'rgba(79,70,229,0.06)',
                  }}
                >
                  <span
                    style={{
                      width: 6, height: 6,
                      borderRadius: '50%',
                      background: 'var(--signal)',
                      boxShadow: '0 0 0 3px rgba(79,70,229,0.20)',
                      display: 'inline-block',
                      flexShrink: 0,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: 'JetBrains Mono, monospace',
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--signal)',
                      letterSpacing: '0.12em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {t('home.welcome')}
                  </span>
                </div>

                <h1
                  style={{
                    fontFamily: 'Plus Jakarta Sans, sans-serif',
                    fontSize: 'clamp(36px, 5vw, 62px)',
                    fontWeight: 800,
                    color: 'var(--text-primary)',
                    letterSpacing: '-0.03em',
                    lineHeight: 1.06,
                    marginBottom: 24,
                  }}
                >
                  {t('home.yourCreative')}
                  <br />
                  <span
                    style={{
                      background: 'linear-gradient(135deg, var(--signal) 0%, var(--signal-bright) 55%, #3b82f6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >{t('home.yourCreativeLibrary')}</span>
                  <span style={{ color: 'var(--text-secondary)', opacity: 0.25 }}>.</span>
                </h1>

                <p
                  style={{
                    fontSize: 15,
                    color: 'var(--text-secondary)',
                    lineHeight: 1.72,
                    maxWidth: 400,
                    marginBottom: 36,
                  }}
                >
                  {t('home.discoverShare')}
                </p>

                <div className="flex items-center gap-3">
                  <Link to="/prompts" className="btn btn-signal">
                    {t('home.explorePrompts')}
                  </Link>
                  <Link to="/register" className="btn btn-ghost">
                    {t('home.startContributing')}
                  </Link>
                </div>

                {/* Inline social proof */}
                <div
                  className="flex items-center gap-3 mt-8"
                  style={{ paddingTop: 24, borderTop: '1px solid var(--border-default)' }}
                >
                  <div className="flex -space-x-1.5">
                    {['A', 'B', 'C', 'D'].map((l) => (
                      <div
                        key={l}
                        style={{
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          background: 'var(--graphite)',
                          border: '1.5px solid var(--surface-1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'Plus Jakarta Sans, sans-serif',
                          fontSize: 9,
                          fontWeight: 600,
                          color: 'var(--text-secondary)',
                        }}
                      >
                        {l}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right — terminal preview */}
              <div className="hidden lg:block" style={{ position: 'relative' }}>
                <TerminalPreview prompt={featuredPrompts[0]} />
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section style={{ padding: '60px 0 40px' }}>
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <div className="animate-fade-up">
              <h1
                style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: 'clamp(28px, 4vw, 42px)',
                  fontWeight: 700,
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.03em',
                  marginBottom: 8,
                }}
              >
                {t('home.welcomeBack', { username: user?.username })}
              </h1>
              <p
                style={{
                  fontSize: 15,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.6,
                }}
              >
                {t('home.continueExploring')}
              </p>
            </div>

            {/* Quick action cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginTop: 28 }}>
              {[
                {
                  to: '/create',
                  label: t('home.sharePrompt'),
                  sublabel: 'Publica tu prompt',
                  accent: 'var(--signal)',
                  accentDim: 'var(--signal-dim)',
                  icon: (
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  ),
                },
                {
                  to: '/prompts',
                  label: 'Explorar prompts',
                  sublabel: 'Descubre los mejores',
                  accent: '#3b82f6',
                  accentDim: 'rgba(59, 130, 246, 0.10)',
                  icon: (
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                    </svg>
                  ),
                },
                {
                  to: '/tools',
                  label: t('navigation.tools'),
                  sublabel: 'Directorio de IA',
                  accent: 'var(--cyan)',
                  accentDim: 'var(--cyan-dim)',
                  icon: (
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                },
                {
                  to: '/blog',
                  label: t('navigation.blog'),
                  sublabel: 'Comunidad & tutoriales',
                  accent: '#3b82f6',
                  accentDim: 'rgba(59,130,246,0.08)',
                  icon: (
                    <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                  ),
                },
              ].map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{
                    padding: '16px',
                    background: 'var(--surface-1)',
                    border: `1px solid var(--border-default)`,
                    borderRadius: 'var(--r-md)',
                    textDecoration: 'none',
                    transition: 'all 200ms cubic-bezier(0.22,1,0.36,1)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                  onMouseEnter={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = link.accent;
                    el.style.boxShadow = `0 4px 16px ${link.accentDim}, 0 0 0 3px ${link.accentDim}`;
                    el.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={e => {
                    const el = e.currentTarget as HTMLElement;
                    el.style.borderColor = 'var(--border-default)';
                    el.style.boxShadow = 'none';
                    el.style.transform = 'none';
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 'var(--r-sm)',
                    background: link.accentDim,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: link.accent,
                  }}>
                    {link.icon}
                  </div>
                  <div>
                    <p style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em', marginBottom: 2 }}>
                      {link.label}
                    </p>
                    <p style={{ fontSize: 11, color: 'var(--text-secondary)', opacity: 0.65 }}>
                      {link.sublabel}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ─── Categories Strip ──────────────────────────────────── */}
      <div style={{ borderTop: '1px solid var(--border-default)', borderBottom: '1px solid var(--border-default)' }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div
            className="flex items-center gap-2 overflow-x-auto"
            style={{ padding: '12px 0', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories.map((cat, i) => (
              <Link
                key={cat.value}
                to={`/prompts?category=${cat.value}`}
                className="category-pill animate-fade-in"
                style={{ animationDelay: `${i * 40}ms` }}
              >
                <cat.icon className="h-3.5 w-3.5 flex-shrink-0" />
                {cat.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Main Content ──────────────────────────────────────── */}
      <div
        className="max-w-7xl mx-auto px-5 sm:px-8"
        style={{ paddingTop: 72, paddingBottom: 96 }}
      >

        {/* Featured */}
        {featuredPrompts.length > 0 && (
          <section style={{ marginBottom: 72 }}>
            <SectionHeader
              label={t('home.featured')}
              title={t('home.handPickedPrompts')}
              subtitle={t('home.curatedForQuality')}
              href="/prompts?featured=true"
            />
            <PromptGrid prompts={featuredPrompts} />
          </section>
        )}

        <hr className="rule" style={{ marginBottom: 72 }} />

        {/* Ad — between sections */}
        <div style={{ marginBottom: 72, borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          <AdSenseAd adSlot="7259836401" adFormat="horizontal" />
        </div>

        {/* Featured Projects Ad */}
        {featuredProjects.length > 0 && (featuredProjects[0].placements?.justShared ?? false) && (
          <FeaturedProjectAdCard project={featuredProjects[0]} />
        )}

        {/* Recent */}
        {recentPrompts.length > 0 && (
          <section style={{ marginBottom: 72 }}>
            <SectionHeader
              label={t('home.recent')}
              title={t('home.justShared')}
              subtitle={t('home.latestContributions')}
              href="/prompts?sort=newest"
              linkLabel={t('home.viewAll')}
            />
            <PromptGrid prompts={recentPrompts} />
          </section>
        )}

        {/* CTA — only for guests */}
        {!isAuthenticated && (
          <div
            style={{
              position: 'relative',
              borderRadius: 'var(--r-xl)',
              background: 'var(--surface-1)',
              border: '1px solid var(--border-default)',
              padding: 'clamp(36px, 5vw, 64px)',
              textAlign: 'center',
              overflow: 'hidden',
            }}
          >
            {/* Ambient glow inside CTA */}
            <div
              style={{
                position: 'absolute',
                top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '70%', height: '200%',
                background: 'radial-gradient(ellipse, rgba(79,70,229,0.09) 0%, transparent 65%)',
                pointerEvents: 'none',
              }}
            />
            <div style={{ position: 'relative', zIndex: 1 }}>
              <span className="mono-label" style={{ marginBottom: 16 }}>{t('home.contribute')}</span>
              <h2
                style={{
                  fontSize: 'clamp(22px, 3vw, 36px)',
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  letterSpacing: '-0.026em',
                  maxWidth: 480,
                  margin: '0 auto 16px',
                }}
              >
                {t('home.shareYourBest')}
              </h2>
              <p
                style={{
                  fontSize: 14,
                  color: 'var(--text-secondary)',
                  maxWidth: 360,
                  lineHeight: 1.72,
                  margin: '0 auto 32px',
                }}
              >
                {t('home.joinThousands')}
              </p>
              <div className="flex items-center gap-3 justify-center">
                <Link to="/register" className="btn btn-signal">
                  {t('home.startForFree')}
                </Link>
                <Link to="/prompts" className="btn btn-ghost">
                  {t('home.exploreFirst')}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
